import { useMemo } from "react";
import { centsToDollars, multiplyMoney } from "@/lib/price-utils";

// Union type for products that can be either quote products or selected products with pricing
type ProductSelection = { quantity: number; unitPrice?: number };

// Helper to check if a product has pricing information
const hasUnitPrice = (
  product: ProductSelection
): product is ProductSelection & { unitPrice: number } => {
  return "unitPrice" in product && typeof product.unitPrice === "number";
};

interface UseProductSummaryOptions {
  selectedProducts?: ProductSelection[];
  showPricing?: boolean;
}

interface ProductSummary {
  totalItems: number;
  totalValue: number;
  totalValueCents: number;
  hasMissingPrices: boolean;
}

/**
 * Hook to calculate summary statistics for selected products
 * Extracted from SelectedProductsList for reuse
 */
export function useProductSummary({
  selectedProducts,
  showPricing = false,
}: UseProductSummaryOptions): ProductSummary {
  return useMemo(() => {
    const totalItems = (selectedProducts || []).reduce((sum, product) => {
      const quantity = product.quantity;
      return sum + quantity;
    }, 0);

    let totalValueCents = 0;
    let hasMissingPrices = false;

    if (showPricing) {
      (selectedProducts || []).forEach((product) => {
        if (hasUnitPrice(product)) {
          // unitPrice is already in cents, multiply by quantity
          const productTotalCents = multiplyMoney(
            product.unitPrice,
            product.quantity
          );
          totalValueCents += productTotalCents;
        } else {
          hasMissingPrices = true;
        }
      });
    }

    const totalValue = centsToDollars(totalValueCents);
    return { totalItems, totalValue, totalValueCents, hasMissingPrices };
  }, [selectedProducts, showPricing]);
}
